/**
 * The API tab's rail (design "API tab", left): a find box, then every service with its endpoints, labelled by where
 * they come from ("OpenAPI · 4", "from code · 2"; the app accounts' routes marked "auth"), then History (the last
 * calls from this browser, with their status). A stopped service says so: its calls need its preview running.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { matchEndpoint, methodClass } from '../../utils/apiTesterModel';
import ServiceStartButton from '../sandbox/ServiceStartButton';

const SOURCE_LABELS = { openapi: 'OpenAPI', code: 'from code' };

export function MethodTag({ method }) {
  return <span className={`api-method ${methodClass(method)}`}>{method}</span>;
}

MethodTag.propTypes = { method: PropTypes.string.isRequired };

export function StatusPill({ status, error = null }) {
  if (!status) { return <span className="data-pill is-bad-text" title={error || undefined}>failed</span>; }
  const tone = status < 300 ? 'is-ok' : status < 400 ? '' : status < 500 ? 'is-warn' : 'is-bad';
  return <span className={`data-pill ${tone}`}>{status}</span>;
}

StatusPill.propTypes = { error: PropTypes.string, status: PropTypes.number };

export default function ApiRail({
  services, find, onFind, selectedKey, onSelect, history, onPickHistory, onClearHistory, projectId = null, canStart = false, onStarted = undefined,
}) {
  const groups = services.map((service) => ({ service, endpoints: service.endpoints.filter((endpoint) => matchEndpoint(endpoint, find)) }))
    .filter((group) => !find || group.endpoints.length > 0);
  return (
    <aside aria-label="Endpoints" className="api-rail">
      <label className="data-sr" htmlFor="api-find">Find an endpoint</label>
      <input className="data-input is-wide" id="api-find" onChange={(event) => onFind(event.target.value)} placeholder="Find an endpoint…" type="search" value={find} />
      {groups.map(({ service, endpoints }) => (
        <div aria-label={service.name} className="api-group" key={service.service} role="group">
          <div className="api-group-label">
            <span>{service.name}</span>
            <span className="api-group-source">
              {service.endpoints.length ? `${SOURCE_LABELS[service.source] || service.source} · ${service.endpoints.length}` : 'no routes found'}
            </span>
          </div>
          {!service.running && (
            <div className="api-group-note api-group-stopped">
              <span>{service.state === 'building' ? 'Starting…' : 'Not running: start its preview to call it'}</span>
              {/* Started in place: no need to leave the tab. */}
              {canStart && projectId && service.state !== 'building' && (
                <ServiceStartButton onStarted={onStarted} projectId={projectId} serviceName={service.service} />
              )}
            </div>
          )}
          {endpoints.map((endpoint) => {
            const key = `${service.service} ${endpoint.method} ${endpoint.path}`;
            return (
              <button
                aria-current={selectedKey === key ? 'true' : undefined}
                className={`api-ep${selectedKey === key ? ' is-on' : ''}`}
                data-testid="api-endpoint"
                key={key}
                onClick={() => onSelect(service, endpoint)}
                title={endpoint.summary || (endpoint.file ? `${endpoint.file}${endpoint.line ? `:${endpoint.line}` : ''}` : undefined)}
                type="button"
              >
                <MethodTag method={endpoint.method} />
                <span className="api-ep-path">{endpoint.path}</span>
                {endpoint.origin === 'auth' && <span className="api-ep-tag">auth</span>}
                {endpoint.auth && endpoint.origin !== 'auth' && <span className="api-ep-tag" title={`Needs ${endpoint.auth.replace('role:', 'the role ')}`}>{endpoint.auth === 'user' ? 'user' : endpoint.auth.replace('role:', '')}</span>}
              </button>
            );
          })}
        </div>
      ))}
      {!groups.length && <p className="api-group-note">{find ? `No endpoint matches "${find}"` : 'This project has no services yet.'}</p>}
      <div aria-label="History" className="api-group" role="group">
        <div className="api-group-label">
          <span>History</span>
          {history.length > 0 && <button className="data-link api-clear" onClick={onClearHistory} type="button">Clear</button>}
        </div>
        {!history.length && <p className="api-group-note">Your calls show here (in this browser only).</p>}
        {history.slice(0, 20).map((entry) => (
          <button className="api-ep" data-testid="api-history" key={entry.id} onClick={() => onPickHistory(entry)} title={`${entry.service.replace(/^services\//, '')} · ${new Date(entry.at).toLocaleString()}`} type="button">
            <MethodTag method={entry.method} />
            <span className="api-ep-path">{entry.path}</span>
            <StatusPill error={entry.error} status={entry.status} />
          </button>
        ))}
      </div>
      <p className="data-footnote">Endpoints come from each service&apos;s OpenAPI, else from its routes in the code. Calls go to the running preview, inside your sandbox.</p>
    </aside>
  );
}

ApiRail.propTypes = {
  canStart: PropTypes.bool,
  onStarted: PropTypes.func,
  projectId: PropTypes.string,
  find: PropTypes.string.isRequired,
  history: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClearHistory: PropTypes.func.isRequired,
  onFind: PropTypes.func.isRequired,
  onPickHistory: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedKey: PropTypes.string,
  services: PropTypes.arrayOf(PropTypes.object).isRequired,
};
